import { useState, useCallback, useEffect } from 'react';
import type { CampaignCalendar, CalendarMonth } from '@roll1/shared';
import { LIMITS } from '@roll1/shared';
import { Sparkles, Loader2, Trash2, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useI18n } from '../i18n';

interface CalendarAiTabProps {
  existingCalendar?: CampaignCalendar;
  onCalendarGenerated: (calendar: CampaignCalendar) => void;
  aiAvailable: boolean;
  /** Callback to request AI generation via parent (sends to AiChatPanel) */
  onRequestGeneration?: (worldDescription: string) => void;
  /** AI-generated calendar preview (set by parent after AI responds) */
  aiPreviewCalendar?: CampaignCalendar | null;
}

export default function CalendarAiTab({
  existingCalendar,
  onCalendarGenerated,
  aiAvailable,
  onRequestGeneration,
  aiPreviewCalendar,
}: CalendarAiTabProps) {
  const { t } = useI18n();
  const [worldDescription, setWorldDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [preview, setPreview] = useState<CampaignCalendar | null>(aiPreviewCalendar ?? null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const descValid = worldDescription.trim().length >= 10 && worldDescription.length <= 3000;

  const handleGenerate = useCallback(() => {
    if (!descValid) return;
    setError(null);

    if (onRequestGeneration) {
      setIsGenerating(true);
      onRequestGeneration(worldDescription);
      // Parent will set aiPreviewCalendar when AI responds
      // For now we simulate a local preview after a small delay
      // The actual flow will work through the AI tool pipeline
      setIsGenerating(false);
    }
  }, [descValid, worldDescription, onRequestGeneration]);

  // Sync preview from parent prop when AI responds
  useEffect(() => {
    if (aiPreviewCalendar) {
      setPreview(aiPreviewCalendar);
      setIsGenerating(false);
    }
  }, [aiPreviewCalendar]);

  // Update preview when parent provides AI result
  const displayPreview = preview ?? aiPreviewCalendar;

  const handleAccept = useCallback(() => {
    if (!displayPreview) return;
    if (existingCalendar) {
      setConfirmOpen(true);
    } else {
      onCalendarGenerated(displayPreview);
    }
  }, [displayPreview, existingCalendar, onCalendarGenerated]);

  const handleConfirmReplace = useCallback(() => {
    if (!displayPreview) return;
    onCalendarGenerated(displayPreview);
    setConfirmOpen(false);
  }, [displayPreview, onCalendarGenerated]);

  const handleRegenerate = useCallback(() => {
    setPreview(null);
    handleGenerate();
  }, [handleGenerate]);

  // Month editing in preview
  const updateMonth = useCallback((index: number, changes: Partial<CalendarMonth>) => {
    setPreview(prev => {
      if (!prev) return prev;
      const months = [...prev.months];
      months[index] = { ...months[index], ...changes };
      return { ...prev, months };
    });
  }, []);

  const addMonth = useCallback(() => {
    setPreview(prev => {
      if (!prev || prev.months.length >= LIMITS.MAX_CALENDAR_MONTHS) return prev;
      return { ...prev, months: [...prev.months, { name: 'New Month', days: 30 }] };
    });
  }, []);

  const removeMonth = useCallback((index: number) => {
    setPreview(prev => {
      if (!prev) return prev;
      return { ...prev, months: prev.months.filter((_, i) => i !== index) };
    });
  }, []);

  if (!aiAvailable) {
    return (
      <div className="text-center py-4 text-ui text-muted-foreground">
        {t('calendarAi.unavailable')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* [STYLE_TODO] calendar AI tab styling */}

      {!displayPreview && (
        <>
          <div>
            <Label htmlFor="cal-ai-desc">{t('calendarAi.worldDescription')}</Label>
            <Textarea
              id="cal-ai-desc"
              value={worldDescription}
              onChange={e => setWorldDescription(e.target.value)}
              placeholder={t('calendarAi.worldPlaceholder')}
              maxLength={3000}
              rows={4}
              className="mt-1"
            />
            <p className="text-meta text-muted-foreground mt-1">
              {worldDescription.length}/3000
              {worldDescription.trim().length > 0 && worldDescription.trim().length < 10 && (
                <span className="text-warn ml-2">{t('calendarAi.minChars')}</span>
              )}
            </p>
          </div>

          {error && (
            <p className="text-ui text-danger">{error}</p>
          )}

          <Button
            onClick={handleGenerate}
            disabled={!descValid || isGenerating}
            className="w-full gap-2"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('calendarAi.generating')}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {t('calendarAi.generate')}
              </>
            )}
          </Button>
        </>
      )}

      {displayPreview && (
        <div className="space-y-3">
          <h4 className="text-ui font-medium">{t('calendarAi.preview')}</h4>

          <div>
            <Label htmlFor="cal-ai-name">{t('calendarAi.calendarName')}</Label>
            <Input
              id="cal-ai-name"
              value={displayPreview.name}
              onChange={e => setPreview({ ...displayPreview, name: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="cal-ai-year-label">{t('calendarAi.yearLabel')}</Label>
            <Input
              id="cal-ai-year-label"
              value={displayPreview.yearLabel ?? ''}
              onChange={e => setPreview({ ...displayPreview, yearLabel: e.target.value })}
              placeholder="DR, YK, AD..."
            />
          </div>

          <div>
            <Label htmlFor="cal-ai-epoch">{t('calendarAi.epoch')}</Label>
            <Input
              id="cal-ai-epoch"
              value={displayPreview.epoch ?? ''}
              onChange={e => setPreview({ ...displayPreview, epoch: e.target.value })}
            />
          </div>

          {/* Months table */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>{t('calendarAi.months')}</Label>
              <Button variant="ghost" size="sm" onClick={addMonth}>
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {displayPreview.months.map((month, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={month.name}
                    onChange={e => updateMonth(i, { name: e.target.value })}
                    className="flex-1 h-7 text-meta"
                  />
                  <Input
                    type="number"
                    value={month.days}
                    onChange={e => updateMonth(i, { days: Number(e.target.value) })}
                    className="w-16 h-7 text-meta"
                    min={1}
                    max={LIMITS.MAX_CALENDAR_MONTH_DAYS}
                  />
                  <Button variant="ghost" size="sm" className="w-6 p-0" onClick={() => removeMonth(i)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={handleRegenerate}>
              {t('calendarAi.regenerate')}
            </Button>
            <Button onClick={handleAccept}>
              {t('calendarAi.accept')}
            </Button>
          </div>
        </div>
      )}

      {/* Confirm replace dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('calendarAi.replaceTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-ui">{t('calendarAi.replaceWarning')}</p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {t('timelineBatch.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleConfirmReplace}>
              {t('calendarAi.replaceConfirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
